import { maxAnnouncementBytes, type RecordingNoticeSettings } from '@smarton-pbx/shared';
import { Megaphone, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { Loading, Notice, Pill } from '../components/ui';
import { language, rich, t } from '../i18n';
import { useLoad } from '../lib/hooks';

const languageNames = new Intl.DisplayNames([language], { type: 'language' });
const languageList = new Intl.ListFormat(language, { type: 'disjunction' });
const noticeNames = { NONE: t('No notice'), ANNOUNCE: t('Announced to the other party'), REMIND: t('Reminder to staff') } as const;

/** The recording notice: wording, and recorded announcements per language. Admins only. */
export function RecordingNoticeCard() {
  const loaded = useLoad(api.recordingNotice);
  const [settings, setSettings] = useState<RecordingNoticeSettings | null>(null);
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadFor = useRef<string>('');

  useEffect(() => {
    if (loaded.data) {
      setSettings(loaded.data);
      setText(loaded.data.text ?? '');
    }
  }, [loaded.data]);

  // While an upload is being converted, check back.
  useEffect(() => {
    if (!settings?.announcements.some((a) => a.status === 'PENDING')) return;
    const timer = setTimeout(() => void api.recordingNotice().then(setSettings), 1500);
    return () => clearTimeout(timer);
  }, [settings]);

  async function act(language: string, work: () => Promise<RecordingNoticeSettings>) {
    setError(undefined);
    setBusy(language);
    try {
      setSettings(await work());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loaded.error) return <Notice kind="error">{loaded.error}</Notice>;
  if (!settings) return <Loading />;

  const languages = [...new Set([...settings.languages, ...settings.announcements.map((a) => a.language)])];
  const missing = settings.regions.filter(
    (r) => r.recordingNotice === 'ANNOUNCE' && !settings.announcements.some((a) => a.status === 'READY' && r.languages.includes(a.language)),
  );

  return (
    <div className="card">
      <div className="card-header">
        <div className="row" style={{ gap: '0.6rem' }}>
          <Megaphone size={18} className="subtle" aria-hidden />
          <h2>{t('Recording notice')}</h2>
        </div>
      </div>
      <div className="card-body stack">
        <p className="muted">
          {rich(
            'Each region chooses its notice on the {regions} page: none, an announcement the other party hears when the call starts, or a reminder to staff on every call.',
            { regions: <Link to="/regions">{t('Regions')}</Link> },
          )}
        </p>
        <div className="row" style={{ gap: '0.4rem' }}>
          {settings.regions.map((r) => (
            <Pill key={r.id}>
              {r.name}: {noticeNames[r.recordingNotice]}
            </Pill>
          ))}
        </div>

        <label className="field">
          {t('Wording')}
          <textarea
            rows={2}
            value={text}
            maxLength={500}
            placeholder={t('e.g. This call may be recorded and analysed to improve our service.')}
            onChange={(e) => (setSaved(false), setText(e.target.value))}
            data-testid="notice-text"
          />
          <span className="field-hint">{t('Shown to staff as the reminder, and the script for whoever records the announcement.')}</span>
        </label>
        <div>
          <button
            type="button"
            className="secondary small"
            onClick={() => void act('', () => api.updateRecordingNoticeText(text.trim() || null)).then(() => setSaved(true))}
            data-testid="save-notice-text"
          >
            {t('Save wording')}
          </button>
          {saved && <span className="subtle" style={{ marginLeft: '0.75rem' }}>{t('Saved.')}</span>}
        </div>

        <div className="stack" style={{ gap: '0.5rem' }}>
          <div style={{ fontWeight: 560 }}>{t('Announcements')}</div>
          <input
            ref={fileInput}
            type="file"
            accept="audio/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              if (file.size > maxAnnouncementBytes) return setError(t('Use a file under 5 MB.'));
              void act(uploadFor.current, () => api.uploadAnnouncement(uploadFor.current, file));
            }}
            data-testid="announcement-file"
          />
          {languages.map((language) => {
            const announcement = settings.announcements.find((a) => a.language === language);
            return (
              <div key={language} className="announcement-row" data-testid="announcement" data-language={language} data-status={announcement?.status ?? 'NONE'}>
                <span className="announcement-language">{languageNames.of(language)}</span>
                {announcement?.status === 'READY' ? (
                  <audio controls preload="none" src={api.announcementAudioUrl(language)} aria-label={t('{language} announcement', { language: languageNames.of(language) ?? language })} />
                ) : (
                  <span className="subtle grow">
                    {announcement?.status === 'PENDING' ? t('Converting…') : announcement?.status === 'FAILED' ? t('That file couldn’t be used.') : t('None yet')}
                  </span>
                )}
                <button
                  type="button"
                  className="secondary small"
                  disabled={busy === language}
                  onClick={() => {
                    uploadFor.current = language;
                    fileInput.current?.click();
                  }}
                >
                  <Upload size={14} aria-hidden /> {announcement ? t('Replace') : t('Upload')}
                </button>
                {announcement && (
                  <button type="button" className="ghost icon" aria-label={t('Remove the {language} announcement', { language: languageNames.of(language) ?? language })} onClick={() => void act(language, () => api.deleteAnnouncement(language))}>
                    <Trash2 size={15} aria-hidden />
                  </button>
                )}
              </div>
            );
          })}
          <p className="subtle" style={{ fontSize: '0.85rem' }}>
            {t(
              'A short recording (a few seconds) in WAV, MP3, OGG or M4A. Each region plays the one in its main language, or else in another of its languages.',
            )}
          </p>
        </div>
        {missing.map((r) => (
          <Notice key={r.id}>
            {t('{region} is set to announce, but there’s no announcement in {languages} yet, so callers there hear nothing.', {
              region: r.name,
              languages: languageList.format(r.languages.map((l) => languageNames.of(l) ?? l)),
            })}
          </Notice>
        ))}
        {error && <Notice kind="error">{error}</Notice>}
      </div>
    </div>
  );
}
